import eventbus from 'tiny-emitter/instance';
import { TYPE, GRIDSNAP } from '@/constants';
import SectionHelper from '../models/sections/index';
import Application from '../application';
import SVG from 'svg.js';
import { createPathTool } from '../tools/PathTool';
import { useState } from '@/store/useStore';
import PathSection from '../models/sections/path';

const availableTypes = [TYPE.CIRCLE, TYPE.RECT, TYPE.POLYGON, TYPE.PATH];
type Types = (typeof availableTypes)[number];

export default class DrawingSections {
  mouseListenerOptions = {
    passive: false,
    capture: false,
  };

  app: Application;
  svg: SVG.Doc;
  canvas: SVG.G;
  type: Types;
  shape!: SVG.Shape;

  cancelPathDrawing = () => {};

  constructor(app: Application, type: Types) {
    if (availableTypes.indexOf(type) === -1) {
      throw Error(`Section type is not supported: ${type}`);
    }
    this.app = app;
    this.svg = this.app.svg;
    this.canvas = this.app.canvas;
    this.type = type;
    this.setup();
    this.setupEventListeners();

    return this;
  }

  setup() {
    switch (this.type) {
      case TYPE.POLYGON:
        this.initPolygonDrawing();
        break;
      case TYPE.PATH:
        this.initPathDrawing();
        break;
      default:
        this.initSimpleDrawing();
    }
    this.svg.attr({ cursor: 'crosshair' });
  }

  setupEventListeners() {
    this.svg.on('keydown', this.handleKeyDown, this);
  }

  initSimpleDrawing() {
    this.addStandardEventListeners();
  }

  initPolygonDrawing() {
    this.initDrawingShape();
    this.shape.draw({ snapToGrid: 1 });
    this.initPolygonListeners();
  }

  initPathDrawing() {
    if (this.app.seatmap.store.resizedUnit) {
      const section = this.app.seatmap.getSectionById(this.app.seatmap.store.resizedUnit)!;
      if (section.subtype === TYPE.PATH) {
        (section as unknown as PathSection).finishEdit();
      } else {
        section.endResizing();
      }
    }

    const { promise, cancel } = createPathTool(this.app);
    promise.then(({ d, bbox }) => {
      const attributes = {
        path: d,
        position: [bbox.x + bbox.width / 2, bbox.y + bbox.height / 2],
      };
      const sid = (this.app.currentSection && this.app.currentSection.uuid) ?? undefined;

      const unit = SectionHelper.create(this.type, { sid, attributes });
      eventbus.emit('seatmap:add:unit', unit);
      eventbus.emit('refreshUnassignedCount');

      this.app.seatmap.startSeatTransfer(unit.uuid);
    });
    this.cancelPathDrawing = cancel;
  }

  initPolygonListeners() {
    this.shape.on('drawstart', () => {
      eventbus.emit('drawing:started');
      //this.svg.addClass('circle-cursor');
      this.svg.on('dblclick', this.handleDblClick, this);
    });
    this.shape.on('drawstop', this.onPolygonDrawStop, this);
  }

  handleDblClick(e) {
    e.preventDefault();
    this.shape.draw('done');
    this.shape.off('drawstart');
  }

  onPolygonDrawStart() {
    eventbus.emit('drawing:started');
    this.addPolygonEventListeners();
  }

  onPolygonDrawStop() {
    this.handleDrawStop();
    //this.svg.removeClass('circle-cursor');
    this.removePolygonEventListeners();
  }

  handleDrawStop() {
    this.shape.removeClass('no-fill');
    const attributes = {};
    const sid = this.app.currentSection && this.app.currentSection.uuid;
    switch (this.type) {
      case TYPE.CIRCLE: {
        attributes.position = [this.shape.attr('cx'), this.shape.attr('cy')];
        attributes.radius = this.shape.attr('r');
        break;
      }
      case TYPE.RECT: {
        const { width, height } = this.shape.attr();
        attributes.position = [this.shape.cx(), this.shape.cy()];
        attributes.radius = this.shape.attr('rx');
        attributes.width = width;
        attributes.height = height;
        break;
      }
      case TYPE.POLYGON: {
        attributes.shape = this.shape.array();
        //attributes.shape = this.shape.attr('points');
        attributes.position = [this.shape.cx(), this.shape.cy()];
        break;
      }
      default:
        break;
    }
    if (this.app.seatmap.sections.length > 0) {
      this.app.seatmap.sections.every((section) => {
        if (section.attributes.scale) {
          attributes.scale = section.attributes.scale;
          return false;
        }
        return true;
      });
    }
    const unit = SectionHelper.create(this.type, { sid, attributes });
    eventbus.emit('seatmap:add:unit', unit);
    eventbus.emit('refreshUnassignedCount');

    this.app.seatmap.startSeatTransfer(unit.uuid);
  }

  addPolygonEventListeners() {
    this.svg.on('dblclick', this.handlePolygonDblclick, this);
    this.svg.on('keydown', this.handlePolygonKeydown, this);
    this.svg.on('keyup', this.handlePolygonKeyup, this);
  }

  handlePolygonDblclick() {
    this.shape.draw('stop');
    this.shape.draw('done');
  }

  handlePolygonKeydown(e) {
    // snapping to grid while holding Ctrl button
    if (e.keyCode === 17) {
      this.shape.draw('param', 'snapToGrid', GRIDSNAP);
    }
  }

  handlePolygonKeyup() {
    this.shape.draw('param', 'snapToGrid', 1);
  }

  addStandardEventListeners() {
    this.svg.on('mousedown', this.handleMouseDown, this);
    this.svg.on('mouseup', this.handleMouseUp, this);
    this.svg.on('mousemove', this.handleMouseMove, this);
  }

  handleKeyDown({ key }) {
    switch (key) {
      case 'Escape':
        this.shape.draw('cancel');
        break;
      case 'Control':
        this.shape.draw('param', 'snapToGrid', GRIDSNAP);
        break;
      default:
        break;
    }
  }

  handleMouseDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    this.initDrawingShape();
    this.shape.draw(e);
    this.shape.on('drawstop', this.handleDrawStop, this);
    eventbus.emit('drawing:started');
  }

  handleMouseUp(e) {
    if (e.button !== 0) return;
    this.shape.draw('stop', e);
    this.shape.off('drawstop');
  }

  handleMouseMove(e) {
    e.preventDefault();
  }

  onDrawStop() {
    // do something useful
  }

  initDrawingShape() {
    // @ts-expect-error
    this.shape = this.canvas[this.type]().addClass(`section-shape section-shape-${this.type} no-fill`);
  }

  destroyEventListeners() {
    this.removeStandardEventListeners();
    this.removePolygonEventListeners();
    this.svg.off('keydown', this.handleKeyDown);
  }

  removeStandardEventListeners() {
    this.svg.off('mousedown', this.handleMouseDown);
    this.svg.off('mouseup', this.handleMouseUp);
    this.svg.off('mousemove', this.handleMouseMove);
  }

  removePolygonEventListeners() {
    this.svg.off('dblclick', this.handleDblClick);
    this.svg.off('dblclick', this.handlePolygonDblclick);
    this.svg.off('keydown', this.handlePolygonKeydown);
    this.svg.off('keyup', this.handlePolygonKeyup);
    if (this.shape) {
      this.shape.off('drawstart');
      this.shape.off('drawstop');
      this.shape.draw('cancel');
    }
  }

  destroy() {
    this.cancelPathDrawing();
    this.destroyEventListeners();
    if (this.shape) this.shape.remove();
    this.svg.attr({ cursor: 'default' });
  }
}
